import { useCallback, useEffect } from 'react';
import { ui } from '@procuros/datachecks';

import { useErrorContext } from 'contexts/ErrorsNavigationContext/ErrorsNavigationContext';
import { WebEDIError } from 'services/webediErrors/types';
import { useWebEDIStore } from 'stores/webedi/webedi';
import { FieldState } from 'components/Form/types';
import { Translate } from 'components/Translate/Translate';
import { useEnumerableOptions } from 'components/WebEDI/helpers';

import { Radio } from '../Radio/Radio';

type SchemableFormRadioGroupProps = {
  field: ui.RadioField;
  value: string;
  onChange: (value: string) => void;
  onBlur: () => void;
  error: WebEDIError | undefined;
  totalNumberOfErrors: number;
  onShowFieldErrorModal?: (showFieldErrorModal: boolean) => void;
  required: boolean;
  disabled: boolean;
  fieldState: FieldState;
  variant?: 'large';
  name: string;
};

export const SchemableFormRadioGroup = ({
  field,
  value,
  onChange,
  onBlur,
  error,
  totalNumberOfErrors,
  onShowFieldErrorModal,
  required,
  disabled,
  fieldState,
  variant,
  name,
}: SchemableFormRadioGroupProps) => {
  const { options } = useEnumerableOptions(field.type_config);
  const isValidating = useWebEDIStore((state) => state.isValidating);
  const { ErrorModalComponent, useErrorModal } = useErrorContext();
  const { showErrorModal, setShowErrorModal, floatingStyles, refs } = useErrorModal();
  const isLargeVariant = variant === 'large';

  const onRadioFocus = useCallback(() => {
    setShowErrorModal(true);
  }, [setShowErrorModal]);

  const onRadioBlur = useCallback(() => {
    onBlur();
    setShowErrorModal(false);
  }, [onBlur, setShowErrorModal]);

  useEffect(() => {
    if (error && showErrorModal) {
      onShowFieldErrorModal?.(true);
    }
  }, [error, showErrorModal, onShowFieldErrorModal]);

  const inlineErrors =
    isLargeVariant && error?.description ? { message: error.description, type: error.type ?? 'error' } : undefined;

  return (
    <div ref={isLargeVariant ? undefined : refs.setReference}>
      <Radio.Group
        label={field.label}
        legendVariant={isLargeVariant ? 'large' : 'default'}
        required={required}
        description={field.description ? <Translate i18nKey={field.description} /> : undefined}
      >
        {options.map((option) => (
          <Radio.Item
            key={option.value}
            name={name}
            value={option.value}
            label={option.label}
            checked={value === option.value}
            onChange={() => onChange(option.value)}
            onFocus={isLargeVariant ? undefined : onRadioFocus}
            onBlur={isLargeVariant ? undefined : onRadioBlur}
            disabled={disabled}
            hasErrors={fieldState === 'error'}
          />
        ))}
      </Radio.Group>

      {inlineErrors && <p className="mt-1 text-sm text-red-700">{inlineErrors.message}</p>}

      {!isLargeVariant && showErrorModal && ErrorModalComponent && (
        <ErrorModalComponent
          viewMode={false}
          error={error}
          ref={refs.setFloating}
          style={floatingStyles}
          numberOfErrors={totalNumberOfErrors}
          isValidating={isValidating}
          isPendingValidation={fieldState === 'pending-validation'}
          fieldDescription={field.description}
        />
      )}
    </div>
  );
};

SchemableFormRadioGroup.displayName = 'SchemableFormRadioGroup';
